import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { ArrowRight, Bot, CheckCircle2, ClipboardCheck, Database, GitBranch, Globe2, Mail, Play, RotateCcw, ShieldCheck, Sparkles, Workflow } from "lucide-react";
import "./styles.css";

type Locale = "zh" | "en";

const tools = ["表单", "CRM", "邮件", "企业微信", "知识库", "数据看板"];

const samples = {
  zh: {
    goal: "每天早上 9 点自动汇总客户反馈表单和 CRM 跟进记录，识别高风险客户，生成项目周报草稿，并在人工确认后发送到企业微信群。",
    policy: "外部发送、客户敏感信息、删除或修改生产数据必须人工审批。"
  },
  en: {
    goal: "Every morning at 9, summarize customer feedback forms and CRM follow-up records, identify high-risk accounts, draft a project report, and send it to WeCom after human approval.",
    policy: "External sending, customer-sensitive information and production data changes require human approval."
  }
};

const copy = {
  zh: {
    brand: "启序自动化",
    lang: "EN",
    nav: ["能力", "编排", "治理"],
    label: "独立案例站点 10 / AI Agent 工作流自动化",
    title: "一个能把业务目标拆成可审计工作流的 AI Agent 网站。",
    subtitle: "面向运营、销售、项目管理和内部协作场景，把重复流程拆成工具调用、人工审批、失败回滚和运行日志。",
    primary: "生成工作流",
    secondary: "查看能力",
    featuresTitle: "Agent 自动化能力",
    featuresSubtitle: "不只是聊天，而是把任务编排成可控执行链路。",
    features: [
      ["目标拆解", "把自然语言目标拆成步骤、输入、输出和依赖。"],
      ["工具编排", "连接表单、CRM、邮件、企业微信、知识库和数据看板。"],
      ["审批边界", "外部发送和敏感操作必须人工确认。"],
      ["失败回滚", "保留中间结果、记录日志并通知负责人。"]
    ],
    deskTitle: "Agent 工作流编排台",
    deskSubtitle: "后端直连 MiniMax，生成可执行但默认需审批的自动化计划。",
    goal: "业务目标",
    policy: "安全策略",
    run: "生成计划",
    sample: "填入示例",
    waiting: "等待业务目标输入。",
    governanceTitle: "自动化治理",
    governanceSubtitle: "真实 Agent 系统必须默认可审计、可暂停、可回滚。",
    panels: [
      ["权限最小化", "每个工具只授予完成任务所需的最小权限。"],
      ["人工确认", "对外发送、财务、删除和生产修改进入审批。"],
      ["运行日志", "记录输入、工具、输出、审批人和失败原因。"]
    ],
    footer: "启序科技 AI 案例站点 · 第 10 个真实独立网站"
  },
  en: {
    brand: "Qixu AgentOps",
    lang: "中文",
    nav: ["Features", "Orchestrate", "Govern"],
    label: "Standalone Case Site 10 / AI Agent Workflow Automation",
    title: "An AI Agent site that turns business goals into auditable workflows.",
    subtitle: "For ops, sales and project teams: convert repeatable processes into tool calls, approvals, rollback and logs.",
    primary: "Generate workflow",
    secondary: "View features",
    featuresTitle: "Agent Automation Capabilities",
    featuresSubtitle: "More than chat: controlled task orchestration.",
    features: [
      ["Goal breakdown", "Split natural language goals into steps, inputs, outputs and dependencies."],
      ["Tool orchestration", "Connect forms, CRM, email, WeCom, knowledge bases and dashboards."],
      ["Approval boundaries", "External sending and sensitive actions require human approval."],
      ["Rollback", "Keep intermediate results, log failures and notify owners."]
    ],
    deskTitle: "Agent Workflow Desk",
    deskSubtitle: "The backend calls MiniMax directly to generate executable plans with approval gates.",
    goal: "Business goal",
    policy: "Safety policy",
    run: "Generate plan",
    sample: "Use sample",
    waiting: "Waiting for a business goal.",
    governanceTitle: "Automation Governance",
    governanceSubtitle: "Real Agent systems must be auditable, pausable and rollback-ready by default.",
    panels: [
      ["Least privilege", "Each tool gets only the minimum permission needed."],
      ["Human confirmation", "External sends, finance, deletion and production changes require approval."],
      ["Run logs", "Record inputs, tools, outputs, approvers and failure reasons."]
    ],
    footer: "Qixu Technology AI case site · tenth real standalone website"
  }
};

function App() {
  const [locale, setLocale] = useState<Locale>("zh");
  const t = copy[locale];
  const [goal, setGoal] = useState(samples.zh.goal);
  const [policy, setPolicy] = useState(samples.zh.policy);
  const [selected, setSelected] = useState(tools);
  const [result, setResult] = useState(t.waiting);
  const [meta, setMeta] = useState("");
  const [loading, setLoading] = useState(false);

  function switchLocale() {
    const next = locale === "zh" ? "en" : "zh";
    setLocale(next);
    setGoal(samples[next].goal);
    setPolicy(samples[next].policy);
    setResult(copy[next].waiting);
    setMeta("");
  }

  async function generate() {
    if (!goal.trim() || loading) return;
    setLoading(true);
    setResult(locale === "zh" ? "正在生成 Agent 工作流..." : "Generating Agent workflow...");
    setMeta("");
    try {
      const response = await fetch("/api/plan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ goal, policy, tools: selected, locale })
      });
      const data = await response.json();
      setResult(data.text || t.waiting);
      setMeta(`${data.provider || "unknown"} / ${data.model || "unknown"}`);
    } catch {
      setResult(locale === "zh" ? "网络异常，无法生成计划。" : "Network error. Plan unavailable.");
      setMeta("network fallback");
    } finally {
      setLoading(false);
    }
  }

  function toggleTool(tool: string) {
    setSelected((items) => items.includes(tool) ? items.filter((item) => item !== tool) : [...items, tool]);
  }

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="#top"><span>自</span><strong>{t.brand}</strong></a>
        <nav><a href="#features">{t.nav[0]}</a><a href="#desk">{t.nav[1]}</a><a href="#governance">{t.nav[2]}</a></nav>
        <button className="lang" onClick={switchLocale}><Globe2 size={16} />{t.lang}</button>
      </header>

      <section className="hero" id="top">
        <div>
          <p className="eyebrow"><Sparkles size={16} />{t.label}</p>
          <h1>{t.title}</h1>
          <p className="subtitle">{t.subtitle}</p>
          <div className="actions">
            <a className="button primary" href="#desk">{t.primary}<ArrowRight size={18} /></a>
            <a className="button secondary" href="#features">{t.secondary}</a>
          </div>
        </div>
        <div className="hero-board">
          <div className="board-head"><Bot size={22} />Agent workflow</div>
          <div className="pipeline">
            <div><Workflow size={18} /> Plan</div>
            <div><Database size={18} /> Use tools</div>
            <div><ClipboardCheck size={18} /> Approve</div>
            <div><Play size={18} /> Execute</div>
          </div>
        </div>
      </section>

      <section className="features" id="features">
        <div className="section-copy"><h2>{t.featuresTitle}</h2><p>{t.featuresSubtitle}</p></div>
        <div className="feature-grid">
          {[GitBranch, Workflow, ShieldCheck, RotateCcw].map((Icon, index) => (
            <article key={t.features[index][0]}><Icon size={24} /><h3>{t.features[index][0]}</h3><p>{t.features[index][1]}</p></article>
          ))}
        </div>
      </section>

      <section className="desk-section" id="desk">
        <div className="desk">
          <div className="section-title"><Bot size={22} /><div><h2>{t.deskTitle}</h2><p>{t.deskSubtitle}</p></div></div>
          <label>{t.goal}<textarea value={goal} onChange={(event) => setGoal(event.target.value)} /></label>
          <div className="tool-grid">
            {tools.map((tool) => <button className={selected.includes(tool) ? "active" : ""} key={tool} onClick={() => toggleTool(tool)}>{tool}</button>)}
          </div>
          <label>{t.policy}<textarea className="policy" value={policy} onChange={(event) => setPolicy(event.target.value)} /></label>
          <div className="composer-actions">
            <button onClick={() => { setGoal(samples[locale].goal); setPolicy(samples[locale].policy); }}>{t.sample}</button>
            <button className="primary" onClick={generate} disabled={loading || !goal.trim()}>{t.run}<Sparkles size={16} /></button>
          </div>
        </div>
        <div className="result-card">
          <h3>Agent plan output</h3>
          <p>{result}</p>
          {meta && <span>{meta}</span>}
        </div>
      </section>

      <section className="governance" id="governance">
        <div><h2>{t.governanceTitle}</h2><p>{t.governanceSubtitle}</p></div>
        <div className="stats">
          <div><span>自动流程</span><strong>36</strong><em>运行中</em></div>
          <div><span>审批节点</span><strong>128</strong><em>本月</em></div>
          <div><span>失败恢复</span><strong>99.1%</strong><em>完成</em></div>
        </div>
        <div className="govern-grid">
          {t.panels.map(([title, content]) => <article key={title}><CheckCircle2 size={20} /><h3>{title}</h3><p>{content}</p></article>)}
        </div>
      </section>

      <footer>{t.footer}</footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
